import { lazy, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Navigate, Route, useLocation } from 'react-router-dom'
import { toast } from 'react-toastify'

import useSyncRequestListener from 'src/auth/hooks/useSyncRequestListener'
import { UUID_REGEX } from 'src/constants'
import { routes } from 'src/routes/routes'
import { UserActions, useUserDispatch } from 'src/state/userState'

import { useUser } from '../../../state/userState'
import { getRoutes } from '../routeRegistry'

const Root = lazy(() => import('src/routes/root'))

const Protected = () => {
    const { t } = useTranslation()
    const location = useLocation()
    const userDispatch = useUserDispatch()
    const { authenticated, authenticatedWithFullContext } = useUser()

    // Listen for sync requests from other tabs.
    useSyncRequestListener()

    const appRoutes = useMemo(() => {
        const routes = getRoutes()
        return routes.map((route) => <Route key={route.path.toString()} path={route.path} element={route.element} />)
    }, [])

    useEffect(() => {
        if (!authenticated) {
            toast.info(t('You need to be logged in to see the chosen page.'))
        }
    }, [authenticated])

    // Because the Protected component lives outside the Router, we need to use a regex to check if the current route is the confirm delivery page.
    // When this is the case, the user only needs to be authenticated, not have a business selected.
    const regexURL = new RegExp(routes.CONFIRM_DELIVERY.replace(/:\w+Id/, UUID_REGEX.source))
    const confirmingDelivery = regexURL.test(location.pathname)

    // These routes are part of the login / selection flow, wehre missing context is expected.
    const authFlowPaths = new Set<string>([
        routes.LOGIN,
        routes.DEALER_SELECTOR,
        routes.BUSINESS_UNIT_SELECTOR,
        routes.BUSINESS_UNIT_BRAND_SELECTOR,
    ])

    const isInAuthFlow = authFlowPaths.has(location.pathname)

    // only enforce full context when user tries to access the app area, not during login/selection screens.
    if (authenticated && !confirmingDelivery && !isInAuthFlow && !authenticatedWithFullContext) {
        userDispatch({ type: UserActions.RESET_SESSION })
        return <Navigate to={routes.LOGIN} replace />
    }

    if (authenticated) {
        return <Root appRoutes={appRoutes} confirmingDelivery={confirmingDelivery} />
    }

    return (
        <Navigate
            to={routes.LOGIN}
            state={{ previousRoute: `${location.pathname}${location.search}`, confirmingDelivery }}
        />
    )
}

export default Protected
